import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/Base/Form";
import { Link } from "react-router-dom";
import { Button } from "@/components/Base/Button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/store/useAuth";
import { FieldType, InputFields } from "@/components/Common/form/InputFields";
import { AuthFormHeader } from "@/components/Auth/AuthFormHeader";
import { captureException } from "@/services/sentry";

const formSchema = z.object({
  password: z
    .string()
    .min(8, { message: "Password should be of minimum 8 characters length" }),
  confirmPassword: z
    .string()
    .min(1, { message: "Password confirmation is required" }),
});

const finalSchema = formSchema.refine(
  (data) => data.confirmPassword === data.password,
  {
    message: "Passwords don't match",
    path: ["confirmPassword"], // This targets the error at the confirmPassword field
  }
);

const fields: FieldType<z.infer<typeof formSchema>>[] = [
  {
    name: "password",
    label: "Password",
    type: "password",
    placeholder: "Password",
    required: true,
    autoComplete: "new-password",
  },
  {
    name: "confirmPassword",
    label: "Confirm Password",
    type: "password",
    placeholder: "Confirm Password",
    required: true,
  },
];

const initialValues = {
  password: "",
  confirmPassword: "",
};

export const ResetPasswordForm = ({
  oobCode,
  onSubmit,
  type = "resetPassword",
}: {
  oobCode: string;
  onSubmit: () => void;
  type?: "resetPassword" | "invitedSetPassword";
}) => {
  const form = useForm<z.infer<typeof finalSchema>>({
    resolver: zodResolver(finalSchema),
    defaultValues: initialValues,
  });

  const { authError, resetPassword } = useAuth();

  const handleSubmit = async (values: z.infer<typeof finalSchema>) => {
    try {
      await resetPassword(oobCode, values.password);
      onSubmit();
    } catch (error) {
      captureException(error);
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex min-h-full items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-md space-y-8 rounded-xl border-2 border-white bg-secondary/10 p-8">
            <AuthFormHeader
              title={
                type === "resetPassword" ? "Reset Password" : "Set Password"
              }
              instructions={`Create a new password below. After ${type === "resetPassword" ? "resetting" : "setting"} your password, you will be redirected to the login page.`}
            />
            <div className="mt-6 flex flex-col justify-center gap-4"></div>

            <InputFields fields={fields} form={form} />

            <div className={cn("flex w-96 pb-4", !authError && "invisible")}>
              <div className="w-128 text-destructive">{authError}</div>
            </div>

            <div className={"flex flex-row justify-between"}>
              <Link to="/login">
                <Button type={"button"}>Login</Button>
              </Link>
              <Button variant={"accent"} type={"submit"}>
                {type === "resetPassword" ? "Reset" : "Set"} Password
              </Button>
            </div>
            <div className={"flex flex-row justify-center pt-4"}>
              <p>
                Don&#39;t have an account?{" "}
                <Link to="/signup" className={"underline"}>
                  Register
                </Link>{" "}
                to get started.
              </p>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
